// Stockage réseau distinct de l'ancien stockage municipal, conservé sans migration destructive.
// Seuls les identifiants de contexte et paramètres cryptographiques sont en clair dans IndexedDB.
import { openDB } from 'idb';
import { z } from 'zod';
import type { PhotoLocale } from './photos';
import type { ActionTerrainReseau, TravailTerrainReseau } from '@eec/contrats';
import { schemaActionTerrainReseau } from '@eec/contrats';
export interface OperationLocaleReseau {
  id: string;
  mission_id: string;
  action: ActionTerrainReseau;
  etat: 'attente' | 'envoyee' | 'conflit' | 'abandonnee';
  erreur: string | null;
}
export interface DonneesTerrain {
  photos?: PhotoLocale[];
  travail: TravailTerrainReseau;
  operations: OperationLocaleReseau[];
  archives?: { archive_le: string; operations: OperationLocaleReseau[] }[];
  missions_a_recharger?: string[];
}
interface Enveloppe {
  id: string;
  version: number;
  sel: Uint8Array<ArrayBuffer>;
  iv: Uint8Array<ArrayBuffer>;
  contenu: ArrayBuffer;
}
export interface CoffreOuvert {
  id: string;
  version: number;
  cle: CryptoKey;
  sel: Uint8Array<ArrayBuffer>;
  donnees: DonneesTerrain;
}
export function archiverOperationsTerminees(d: DonneesTerrain): DonneesTerrain {
  const terminees = d.operations.filter((o) => o.etat === 'envoyee' || o.etat === 'abandonnee');
  if (!terminees.length) return d;
  return {
    ...d,
    operations: d.operations.filter((o) => o.etat !== 'envoyee' && o.etat !== 'abandonnee'),
    archives: [
      ...(d.archives ?? []),
      { archive_le: new Date().toISOString(), operations: terminees },
    ],
  };
}
const NOM = 'eec-terrain-reseau-chiffre-v1';
async function base() {
  return openDB(NOM, 1, {
    upgrade(b) {
      b.createObjectStore('coffres', { keyPath: 'id' });
    },
  });
}
export function contexteTerrain(
  t: Pick<TravailTerrainReseau, 'compte_id' | 'organisation_id' | 'appartenance_id'>,
) {
  return `${t.compte_id}:${t.organisation_id}:${t.appartenance_id}`;
}
export async function listerCoffres(): Promise<string[]> {
  const b = await base();
  try {
    return (await b.getAllKeys('coffres')).map(String);
  } finally {
    b.close();
  }
}
async function deriver(phrase: string, sel: Uint8Array<ArrayBuffer>) {
  if (phrase.length < 12 || phrase.length > 128)
    throw new Error('Utilisez une phrase de déverrouillage de 12 à 128 caractères');
  const materiau = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(phrase),
    'PBKDF2',
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', hash: 'SHA-256', salt: sel, iterations: 310000 },
    materiau,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}
async function chiffrer(c: CoffreOuvert, donnees: DonneesTerrain): Promise<Enveloppe> {
  if (contexteTerrain(donnees.travail) !== c.id) throw new Error('Contexte de coffre incohérent');
  const brut = new TextEncoder().encode(JSON.stringify(donnees));
  if (brut.byteLength > 8 * 1024 * 1024)
    throw new Error('Le coffre dépasse 8 Mio ; conservez la file actuelle avant de poursuivre');
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const contenu = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, additionalData: new TextEncoder().encode(c.id) },
    c.cle,
    brut,
  );
  return { id: c.id, version: c.version + 1, sel: c.sel, iv, contenu };
}
export async function creerCoffre(
  travail: TravailTerrainReseau,
  phrase: string,
): Promise<CoffreOuvert> {
  const sel = crypto.getRandomValues(new Uint8Array(16));
  const cle = await deriver(phrase, sel);
  const c: CoffreOuvert = {
    id: contexteTerrain(travail),
    version: 0,
    sel,
    cle,
    donnees: { travail, operations: [] },
  };
  const enveloppe = await chiffrer(c, c.donnees);
  const b = await base();
  try {
    await b.add('coffres', enveloppe);
  } catch (e) {
    if (e instanceof DOMException && e.name === 'ConstraintError')
      throw new Error(
        'Un coffre existe déjà pour ce contexte. Déverrouillez-le pour le mettre à jour.',
        { cause: e },
      );
    throw e;
  } finally {
    b.close();
  }
  return { ...c, version: 1 };
}
export async function ouvrirCoffre(id: string, phrase: string): Promise<CoffreOuvert> {
  const b = await base();
  let e: Enveloppe | undefined;
  try {
    e = await b.get('coffres', id);
  } finally {
    b.close();
  }
  if (!e) throw new Error('Coffre introuvable');
  return dechiffrerEnveloppe(e, phrase);
}
async function dechiffrerEnveloppe(e: Enveloppe, phrase: string): Promise<CoffreOuvert> {
  const id = e.id;
  const cle = await deriver(phrase, e.sel);
  let donnees: DonneesTerrain;
  try {
    const brut = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: e.iv, additionalData: new TextEncoder().encode(id) },
      cle,
      e.contenu,
    );
    donnees = JSON.parse(new TextDecoder().decode(brut)) as DonneesTerrain;
  } catch {
    throw new Error('Phrase incorrecte ou coffre altéré');
  }
  if (contexteTerrain(donnees.travail) !== id || !Array.isArray(donnees.operations))
    throw new Error('Coffre invalide');
  return { id, version: e.version, sel: e.sel, cle, donnees };
}
function encoder(octets: Uint8Array): string {
  let texte = '';
  for (let i = 0; i < octets.length; i += 8192)
    texte += String.fromCharCode(...octets.subarray(i, i + 8192));
  return btoa(texte);
}
const binaire = z.string().regex(/^[A-Za-z0-9+/]*={0,2}$/);
const schemaSauvegarde = z
  .object({
    format: z.literal('eec-coffre'),
    version: z.literal(1),
    contexte: z.string().max(110),
    sel: binaire.max(24),
    iv: binaire.max(16),
    contenu: binaire.max(12 * 1024 * 1024),
  })
  .strict();
const uuid = z.string().uuid();
const operationSauvee = z.looseObject({
  id: uuid,
  mission_id: uuid,
  action: schemaActionTerrainReseau,
  etat: z.enum(['attente', 'envoyee', 'conflit', 'abandonnee']),
  erreur: z.string().nullable(),
});
const schemaDonneesSauvees = z.looseObject({
  travail: z.looseObject({
    compte_id: uuid,
    organisation_id: uuid,
    appartenance_id: uuid,
    telecharge_le: z.string().datetime({ offset: true }),
    expire_le: z.string().datetime({ offset: true }),
    missions: z.array(
      z.looseObject({
        id: uuid,
        titre: z.string(),
        consignes: z.string(),
        localisation: z.string(),
        incident_reference: z.string(),
        province_code: z.string().optional(),
        province: z.string().optional(),
        statut: z.string(),
        version: z.number().int().positive(),
      }),
    ),
    articles: z.array(
      z.looseObject({ id: uuid, reference: z.string(), libelle: z.string(), unite: z.string() }),
    ),
  }),
  operations: z.array(operationSauvee).max(500),
  archives: z
    .array(
      z.looseObject({
        archive_le: z.string().datetime({ offset: true }),
        operations: z.array(operationSauvee),
      }),
    )
    .optional(),
  missions_a_recharger: z.array(uuid).optional(),
  photos: z
    .array(
      z.looseObject({
        id: uuid,
        mission_id: uuid,
        mission: z.string(),
        base64: binaire.max(1400000),
        empreinte: z.string().regex(/^[a-f0-9]{64}$/),
        taille: z.number().int().positive().max(1048576),
        etat: z.enum(['attente', 'envoyee', 'erreur', 'ecartee']),
        erreur: z.string().nullable(),
        piece_id: uuid.optional(),
      }),
    )
    .max(5)
    .optional(),
});
/** Copie du ciphertext enregistré ; aucune clé ni donnée métier en clair. */
export async function exporterCoffre(c: CoffreOuvert): Promise<string> {
  const b = await base();
  try {
    const e: Enveloppe | undefined = await b.get('coffres', c.id);
    if (!e || e.version !== c.version)
      throw new Error('Le coffre a changé ; déverrouillez-le à nouveau avant de sauvegarder');
    return JSON.stringify({
      format: 'eec-coffre',
      version: 1,
      contexte: e.id,
      sel: encoder(e.sel),
      iv: encoder(e.iv),
      contenu: encoder(new Uint8Array(e.contenu)),
    });
  } finally {
    b.close();
  }
}
export async function restaurerCoffre(
  texte: string,
  phrase: string,
  contexte: string,
  autoriser: () => boolean = () => true,
): Promise<CoffreOuvert> {
  if (texte.length > 12 * 1024 * 1024)
    throw new Error('Sauvegarde trop volumineuse (12 Mio maximum)');
  let s: z.infer<typeof schemaSauvegarde>;
  try {
    s = schemaSauvegarde.parse(JSON.parse(texte));
  } catch {
    throw new Error('Format de sauvegarde invalide ou non pris en charge');
  }
  if (s.contexte !== contexte)
    throw new Error('La sauvegarde appartient à un autre compte ou contexte');
  const decoder = (v: string) => Uint8Array.from(atob(v), (c) => c.charCodeAt(0));
  const sel = decoder(s.sel),
    iv = decoder(s.iv),
    contenu = decoder(s.contenu);
  if (
    sel.length !== 16 ||
    iv.length !== 12 ||
    contenu.length < 16 ||
    contenu.length > 8 * 1024 * 1024 + 16
  )
    throw new Error('Paramètres de sauvegarde invalides');
  const e: Enveloppe = { id: contexte, version: 1, sel, iv, contenu: contenu.buffer };
  const c = await dechiffrerEnveloppe(e, phrase);
  if (!schemaDonneesSauvees.safeParse(c.donnees).success)
    throw new Error('Contenu de sauvegarde invalide');
  const b = await base();
  try {
    if (!autoriser()) throw new Error('Contexte modifié ; restauration annulée');
    // add, jamais put : une restauration ne remplace aucune donnée existante.
    await b.add('coffres', e);
  } catch (err) {
    if (err instanceof DOMException && err.name === 'ConstraintError')
      throw new Error(
        'Un coffre existe déjà pour ce contexte ; restauration refusée sans écrasement',
        { cause: err },
      );
    throw err;
  } finally {
    b.close();
  }
  return c;
}
export async function enregistrerCoffre(
  c: CoffreOuvert,
  donnees: DonneesTerrain,
  autoriser: () => boolean = () => true,
): Promise<CoffreOuvert> {
  const enveloppe = await chiffrer(c, donnees);
  const b = await base();
  try {
    const tx = b.transaction('coffres', 'readwrite');
    const courant: Enveloppe | undefined = await tx.store.get(c.id);
    if (!autoriser()) {
      await tx.done;
      throw new Error('Coffre verrouillé ou contexte modifié ; aucune modification enregistrée');
    }
    if (courant?.version !== c.version) {
      await tx.done;
      throw new Error(
        'Le coffre a changé dans un autre onglet. Verrouillez puis déverrouillez-le avant de poursuivre.',
      );
    }
    await tx.store.put(enveloppe);
    await tx.done;
  } finally {
    b.close();
  }
  return { ...c, version: enveloppe.version, donnees };
}
/** Réencrypte atomiquement sans effacer la file ni prolonger sa validité. */
export async function changerPhraseCoffre(
  c: CoffreOuvert,
  actuelle: string,
  nouvelle: string,
  autoriser: () => boolean = () => true,
): Promise<CoffreOuvert> {
  if (actuelle === nouvelle) throw new Error('Choisissez une phrase différente de la précédente');
  const verifie = await ouvrirCoffre(c.id, actuelle);
  if (verifie.version !== c.version)
    throw new Error('Le coffre a changé dans un autre onglet. Déverrouillez-le à nouveau.');
  const sel = crypto.getRandomValues(new Uint8Array(16));
  const cle = await deriver(nouvelle, sel);
  return enregistrerCoffre({ ...verifie, sel, cle }, verifie.donnees, autoriser);
}
export const ETATS_ACTION_TERRAIN: Record<
  string,
  { depuis: string[]; vers: string; libelle: string }
> = {
  accepter: { depuis: ['ASSIGNED'], vers: 'ACCEPTED', libelle: 'Accepter la mission' },
  commencer: {
    depuis: ['ACCEPTED', 'BLOCKED', 'REJECTED'],
    vers: 'IN_PROGRESS',
    libelle: 'Commencer les travaux',
  },
  bloquer: { depuis: ['ACCEPTED', 'IN_PROGRESS'], vers: 'BLOCKED', libelle: 'Signaler un blocage' },
  rapport: { depuis: ['IN_PROGRESS'], vers: 'REPORTED', libelle: 'Déposer le rapport' },
};
